import { Button, Form } from 'react-bootstrap';
import { Field, type FormikErrors, type FormikTouched } from 'formik';
import { useTranslation } from 'react-i18next';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';
import type { TariffApplicationFormValues } from './model';

interface Props {
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    errors: FormikErrors<TariffApplicationFormValues>;
    touched: FormikTouched<TariffApplicationFormValues>;
}

const fieldError = (
    name: keyof TariffApplicationFormValues,
    errors: FormikErrors<TariffApplicationFormValues>,
    touched: FormikTouched<TariffApplicationFormValues>,
): string => (touched[name] && errors[name] ? String(errors[name]) : '');

const ApplicationForm = ({ handleSubmit, isSubmitting, errors, touched }: Props) => {
    const { t } = useTranslation(['Tariff', 'common']);

    const companyError = fieldError('companyName', errors, touched);
    const taxIdError = fieldError('taxId', errors, touched);
    const phoneError = fieldError('phone', errors, touched);
    const emailError = fieldError('email', errors, touched);
    const addressError = fieldError('legalAddress', errors, touched);
    const representativeError = fieldError('representativeName', errors, touched);

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-create-team-form d-flex flex-column h-100">
                <div className="off-create-team-content px-3">
                    <Field type="hidden" name="planId" />
                    <Field type="hidden" name="planKey" />
                    <Field type="hidden" name="accountUuid" />

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-company">
                            {t('application.companyName')}
                        </label>
                        <Field
                            id="tariff-application-company"
                            name="companyName"
                            className={`form-control ${companyError ? 'is-invalid' : ''}`}
                            autoFocus
                        />
                        {companyError ? <div className="invalid-feedback d-block">{companyError}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-tax">
                            {t('application.taxId')}
                        </label>
                        <Field
                            id="tariff-application-tax"
                            name="taxId"
                            className={`form-control ${taxIdError ? 'is-invalid' : ''}`}
                        />
                        {taxIdError ? <div className="invalid-feedback d-block">{taxIdError}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-phone">
                            {t('application.phone')}
                        </label>
                        <Field
                            id="tariff-application-phone"
                            name="phone"
                            type="tel"
                            className={`form-control ${phoneError ? 'is-invalid' : ''}`}
                        />
                        {phoneError ? <div className="invalid-feedback d-block">{phoneError}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-email">
                            {t('application.email')}
                        </label>
                        <Field
                            id="tariff-application-email"
                            name="email"
                            type="email"
                            className={`form-control ${emailError ? 'is-invalid' : ''}`}
                        />
                        {emailError ? <div className="invalid-feedback d-block">{emailError}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-address">
                            {t('application.legalAddress')}
                        </label>
                        <Field
                            as="textarea"
                            id="tariff-application-address"
                            name="legalAddress"
                            rows={3}
                            className={`form-control ${addressError ? 'is-invalid' : ''}`}
                        />
                        {addressError ? <div className="invalid-feedback d-block">{addressError}</div> : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="tariff-application-representative">
                            {t('application.representativeName')}
                        </label>
                        <Field
                            id="tariff-application-representative"
                            name="representativeName"
                            className={`form-control ${representativeError ? 'is-invalid' : ''}`}
                        />
                        {representativeError ? (
                            <div className="invalid-feedback d-block">{representativeError}</div>
                        ) : null}
                    </Form.Group>
                </div>

                <div className="p-3 offcanvas-footer off-create-team-footer">
                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="info" disabled={isSubmitting} className="min100px">
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="check2-all" className="fs-14 me-2" />
                                    {t('sendRequest')}
                                </>,
                                isSubmitting,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default ApplicationForm;
